import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Button, ErrorAlert, Field, InfoField } from "src/components";
import DropdownAnimation from "src/components/UI/dropdownAnimation/DropdownAnimation";
import { CxmDepositState } from "src/hooks";
import "./cxm-form.scss";

/**
 * Single-screen CXM deposit form: method → amount → submit. There is no payout
 * account here — that is a withdrawal concept.
 *
 * No `Max` control, unlike the payout form. A payout has a real ceiling the
 * user wants to reach (their own balance, held under the method limit); a
 * deposit is bounded only by the method's maximum, which is a limit rather
 * than an intention — nobody deposits "the most the rail allows". The maximum
 * is shown as text under the field, where it belongs.
 *
 * Styles are its own (`cxm-*`); nothing here borrows from the Alfred deposit
 * sheets, so a change to either cannot restyle this flow.
 */
const CxmDepositForm: React.FC<{ deposit: CxmDepositState }> = ({
  deposit: d,
}): JSX.Element => {
  const { t } = useTranslation();

  const methodOptions = useMemo(
    () => d.methods.map((m) => ({ key: m.methodId, value: m.displayName })),
    [d.methods]
  );

  const limitsHint = useMemo(() => {
    const { min, max } = d.limits;
    if (min === null && max === null) return undefined;
    if (max === null) return t("CXM.DEPOSIT.MIN_ONLY", { min, currency: d.currency });
    if (min === null) return t("CXM.DEPOSIT.MAX_ONLY", { max, currency: d.currency });
    return t("CXM.DEPOSIT.LIMITS", { min, max, currency: d.currency });
  }, [d.limits, d.currency, t]);

  if (d.outcome?.kind === "confirmed") {
    return (
      <section className="cxm-form cxm-form--terminal">
        <div className="cxm-success">
          <svg className="cxm-success__mark" viewBox="0 0 56 56" aria-hidden="true">
            <circle className="cxm-success__ring" cx="28" cy="28" r="26" />
            <path className="cxm-success__check" d="M17 29.5 L24.5 37 L39 22" />
          </svg>
        </div>
        <h2 className="cxm-form__title">{t("CXM.DEPOSIT.SUBMITTED_TITLE")}</h2>
        <p className="cxm-form__text">{t("CXM.DEPOSIT.SUBMITTED_TEXT")}</p>
        <InfoField
          label={t("CXM.DEPOSIT.YOU_PAY")}
          value={`${d.outcome.amount} ${d.currency}`}
        />
        <Button title={t("CXM.DEPOSIT.DONE")} className="cxm-button" onClick={d.reset} />
      </section>
    );
  }

  // `form_post` — the gateway wants a browser form POSTed to it, which this
  // widget has no mechanism for. Say so rather than leaving a dead screen.
  if (d.outcome?.kind === "unsupported") {
    return (
      <section className="cxm-form cxm-form--terminal">
        <svg className="cxm-form__blocked-mark" viewBox="0 0 44 44" aria-hidden="true">
          <circle cx="22" cy="22" r="20" fill="none" stroke="currentColor" strokeWidth="2" opacity="0.35" />
          <path d="M22 13v13" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
          <circle cx="22" cy="31.5" r="1.6" fill="currentColor" />
        </svg>
        <h2 className="cxm-form__title">{t("CXM.DEPOSIT.UNSUPPORTED_TITLE")}</h2>
        <p className="cxm-form__text">{t("CXM.DEPOSIT.UNSUPPORTED_TEXT")}</p>
        <Button
          title={t("CXM.DEPOSIT.BACK")}
          className="cxm-button"
          secondary
          onClick={d.reset}
        />
      </section>
    );
  }

  // An empty catalogue means the operator granted no payment rules for this
  // partner/user pair — a normal state, not a failure to retry. A *failed*
  // catalogue call also lands here with zero methods, so it is excluded: the
  // user would otherwise be told deposits are not set up when the real answer
  // is that we could not ask, and the error would never be shown.
  if (!d.methodsLoading && !d.methodsError && d.methods.length === 0) {
    return (
      <section className="cxm-form cxm-form--terminal">
        <svg className="cxm-form__blocked-mark" viewBox="0 0 44 44" aria-hidden="true">
          <circle cx="22" cy="22" r="20" fill="none" stroke="currentColor" strokeWidth="2" opacity="0.35" />
          <path d="M22 13v13" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
          <circle cx="22" cy="31.5" r="1.6" fill="currentColor" />
        </svg>
        <h2 className="cxm-form__title">{t("CXM.DEPOSIT.UNAVAILABLE_TITLE")}</h2>
        <p className="cxm-form__text">{t("CXM.DEPOSIT.UNAVAILABLE_TEXT")}</p>
      </section>
    );
  }

  const amountDisabled = d.submitLoading || !d.selectedMethod;
  const amountError = d.amountTouched ? d.amountError : undefined;

  return (
    <form className="cxm-form" onSubmit={d.handleSubmit}>
      {methodOptions.length > 1 && (
        <div className="cxm-method-row">
          <DropdownAnimation
            label={t("CXM.DEPOSIT.METHOD")}
            selectedValue={d.selectedMethodId ?? ""}
            options={methodOptions}
            placeholder={t("CXM.DEPOSIT.SELECT_METHOD")}
            onChange={(field) => d.setSelectedMethodId(field.key)}
            disabled={d.methodsLoading || d.submitLoading}
          />
        </div>
      )}

      <div className="cxm-amount">
        <Field
          label={t("CXM.DEPOSIT.AMOUNT", { currency: d.currency })}
          value={d.amount}
          onChange={d.handleAmountChange}
          inputMode="decimal"
          disabled={amountDisabled}
          error={amountError}
          /* Limits are a constraint the user needs *while* typing, and the
             error takes the same line — the shared field otherwise shows it
             only in a hover tooltip, which a touch user never opens. */
          assist={amountError ?? limitsHint}
        />
      </div>

      {/* Fee is structurally zero at CXM — stated rather than computed. */}
      <InfoField
        className="info-field--muted cxm-figure"
        label={t("CXM.DEPOSIT.GATEWAY_FEE")}
        value={`0 ${d.currency}`}
      />

      <ErrorAlert message={d.submitError ?? d.methodsError} />

      <Button
        title={t("CXM.DEPOSIT.CONTINUE")}
        className="cxm-button"
        type="submit"
        disabled={d.submitDisabled}
        pending={d.submitLoading}
      />
    </form>
  );
};

export default CxmDepositForm;
